import { Component, type ErrorInfo, type ReactNode, createElement } from 'react';
import axios from 'axios';
import { ZodError } from 'zod';
import { ErrorCode, ERROR_MESSAGES } from '@/config/error-messages';
import { formatZodError } from '@/lib/validation';

// ─── AppError ────────────────────────────────────────────────────

export class AppError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number | null;
  readonly userMessage: string;
  readonly originalError?: unknown;

  constructor(opts: {
    message: string;
    code?: ErrorCode | string;
    status?: number | null;
    userMessage?: string;
    originalError?: unknown;
  }) {
    super(opts.message);
    this.name = 'AppError';
    this.code = opts.code ?? ErrorCode.UNKNOWN;
    this.status = opts.status ?? null;
    this.userMessage = opts.userMessage ?? ERROR_MESSAGES[ErrorCode.UNKNOWN];
    this.originalError = opts.originalError;
  }
}

// ─── Guards & Converters ─────────────────────────────────────────

export function isApiError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Normalises any thrown value into a structured AppError.
 * Handles ZodError, AxiosError, native Error, AppError, and unknown types.
 */
export function toAppError(
  error: unknown,
  fallbackCode: ErrorCode = ErrorCode.UNKNOWN,
): AppError {
  if (error instanceof AppError) return error;

  if (error instanceof ZodError) {
    return new AppError({
      message: formatZodError(error),
      code: ErrorCode.VALIDATION_FAILED,
      status: 400,
      userMessage: formatZodError(error),
      originalError: error,
    });
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? null;
    const raw = error.response?.data;
    const data = typeof raw === 'object' && raw !== null
      ? (raw as { message?: string; code?: string })
      : undefined;
    const serverMessage = data?.message || (typeof raw === 'string' ? raw : undefined);
    const code = data?.code ?? mapHttpStatusToErrorCode(status) ?? fallbackCode;
    return new AppError({
      message: serverMessage || error.message,
      code,
      status,
      userMessage: serverMessage || friendlyMessage(status),
      originalError: error,
    });
  }

  if (error instanceof Error) {
    return new AppError({
      message: error.message,
      code: fallbackCode,
      originalError: error,
    });
  }

  return new AppError({
    message: String(error),
    code: fallbackCode,
    originalError: error,
  });
}

function mapHttpStatusToErrorCode(status: number | null): ErrorCode | undefined {
  switch (status) {
    case 400: return ErrorCode.VALIDATION_FAILED;
    case 401: return ErrorCode.AUTH_FAILED;
    case 403: return ErrorCode.FORBIDDEN;
    case 404: return ErrorCode.NOT_FOUND;
    case 409: return ErrorCode.CONFLICT;
    case 429: return ErrorCode.RATE_LIMIT;
    case 500:
    case 502:
    case 503: return ErrorCode.SERVER_ERROR;
    default: return undefined;
  }
}

function friendlyMessage(status: number | null): string {
  const code = mapHttpStatusToErrorCode(status);
  if (code) return ERROR_MESSAGES[code];
  return ERROR_MESSAGES[ErrorCode.UNKNOWN];
}

// ─── ErrorBoundary ───────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (typeof this.props.fallback === 'function') {
      return this.props.fallback(error, this.reset);
    }

    if (this.props.fallback) return this.props.fallback;

    return createElement(DefaultErrorFallback, { error, onReset: this.reset });
  }
}

function DefaultErrorFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  const message = error instanceof AppError ? error.userMessage : 'Something went wrong.';
  return createElement('div', {
    className: 'flex flex-col items-center justify-center gap-4 p-8 text-center min-h-[300px]',
  },
    createElement('div', { className: 'w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center' },
      createElement('span', { className: 'text-red-400 text-xl' }, '!')
    ),
    createElement('h2', { className: 'text-lg font-semibold text-zinc-100' }, 'Something went wrong'),
    createElement('p', { className: 'text-sm text-zinc-400 max-w-md' }, message),
    createElement('button', {
      onClick: onReset,
      className: 'mt-2 px-4 py-2 rounded-lg bg-zinc-800 text-zinc-100 text-sm hover:bg-zinc-700 transition-colors',
    }, 'Try again')
  );
}
